import { lockBodyScroll } from "../utils/scrollLock";
import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { supabase, getStoredUser } from "../supabase";
import PostTime, { postNameColumn } from "../components/PostTime";
import LikeButton from "../components/LikeButton";
import DownloadButton from "../components/DownloadButton";
import Comments from "../components/Comments";
import { useNavigate } from "react-router-dom";
import { extractHashtags, buildLegacyCaption } from "../utils/hashtags";
import HashtagInput from "../components/HashtagInput";
import ImageLightbox from "../components/ImageLightbox";
import ImageCropModal from "../components/ImageCropModal";
import TruncatedText from "../components/TruncatedText";
import FollowListModal from "../components/FollowListModal";
import ImageCarousel from "../components/ImageCarousel";
import ShareButton from "../components/ShareButton";
import PickerSelect from "../components/PickerSelect";
import Turnstile from "../components/Turnstile";
import { removeStoragePaths, removeDesignStorage, storagePathFromPublicUrl } from "../utils/designStorage";
import PostMenu from "../components/PostMenu";
import Sheet from "../components/Sheet";
import QRCodeModal from "../components/QRCodeModal";
import UndoToast from "../components/UndoToast";
import PostSkeleton from "../components/PostSkeleton";
import VerifiedBadge from "../components/VerifiedBadge";
import ReferralBadge from "../components/ReferralBadge";
import { likeDesign } from "../utils/likes";
import { fetchMyEngagement, attachMyEngagementBindings } from "../utils/myEngagement";
import { subscribeToStatsPolling } from "../utils/statsPolling";
import { enablePush, disablePush, isPushSubscribed, isPushSupported } from "../utils/push";
import { friendlyError } from "../utils/errors";
import { useInstallOption, startInstall } from "../utils/installPrompt";
import { PASSWORD_REQUIREMENTS_TEXT, isPasswordValid } from "../utils/password";
import { clearLocalUserData } from "../utils/clearLocalUserData";
import { readProfileCache, mergeProfileCache } from "../utils/profileCache";
import { withTimeout } from "../utils/timedFetch";
import { useTheme } from "../context/ThemeContext";
import { useFocusTrap } from "../utils/focusTrap";
import { useLanguage, SUPPORTED_LANGUAGES } from "../context/LanguageContext";
import {
  IoCamera,
  IoCheckmarkCircle,
  IoSettings,
  IoCreateOutline,
  IoKeyOutline,
  IoSyncOutline,
  IoLogOutOutline,
  IoTrashOutline,
  IoEllipsisVertical,
  IoCheckmark,
  IoClose,
  IoLockClosedOutline,
  IoDocumentTextOutline,
  IoMailOutline,
  IoShieldCheckmarkOutline,
  IoPinOutline,
  IoQrCodeOutline,
  IoPersonAddOutline,
  IoShareSocialOutline,
  IoEyeOutline,
  IoNotificationsOutline,
  IoMoonOutline,
  IoLanguageOutline,
  IoPhonePortraitOutline,
  IoHelpCircleOutline,
  IoWarningOutline,
} from "react-icons/io5";
import { PiChatCircleBold, PiHeartFill, PiDownloadSimpleBold } from "react-icons/pi";

const USERNAME_REGEX = /^[a-z0-9._]{3,20}$/;
const GENDER_OPTIONS = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "prefer_not_to_say", label: "Prefer not to say" }
];
const PAGE_SIZE = 10;
const TITLE_LIMIT = 500; // matches designs.title's limit in Upload.jsx

function Profile() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();

  const [user, setUser] = useState(getStoredUser);
  // What this account's profile looked like last time, so the page can show
  // it at once and let the fresh fetch correct it (see profileCache.js).
  const [cached] = useState(() => readProfileCache(getStoredUser()?.id));

  const [designs, setDesigns] = useState([]);
  const [totalLikes, setTotalLikes] = useState(cached?.total_likes || 0);
  const [totalDownloads, setTotalDownloads] = useState(cached?.total_downloads || 0);
  const [postsCount, setPostsCount] = useState(cached?.posts_count || 0);
  const [postsLoading, setPostsLoading] = useState(true);
  const [sortBy, setSortBy] = useState("newest"); // newest | oldest | likes
  const [qrModalOpen, setQrModalOpen] = useState(false);
  // The sort this page is currently showing posts for — loadMorePosts reads
  // this to tell whether sortBy changed out from under an in-flight "load
  // more" fetch (see loadMorePosts below).
  const activeSortRef = useRef(sortBy);

  const sentinelRef = useRef(null);
  const [postsOffset, setPostsOffset] = useState(0);
  const [hasMorePosts, setHasMorePosts] = useState(true);
  const [loadingMorePosts, setLoadingMorePosts] = useState(false);

  const [followerCount, setFollowerCount] = useState(cached?.follower_count || 0);
  const [followingCount, setFollowingCount] = useState(cached?.following_count || 0);
  const [followListType, setFollowListType] = useState(null); // null | "followers" | "following"
  const [showFollowers, setShowFollowers] = useState(cached?.show_followers ?? true);
  const [showFollowing, setShowFollowing] = useState(cached?.show_following ?? true);
  const [followersToggleError, setFollowersToggleError] = useState(null);
  const [followingToggleError, setFollowingToggleError] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushToggleError, setPushToggleError] = useState(null);

  // Saved (persisted) profile values
  const [photoURL, setPhotoURL] = useState(cached?.avatar_url || "");
  const [username, setUsername] = useState(cached?.username || "");
  // Until the profile row arrives there's no username to show, and a
  // made-up "@yourname" in its place reads as if that's really their name.
  const [profileLoaded, setProfileLoaded] = useState(!!cached?.username);
  const [bio, setBio] = useState(cached?.bio || "");
  const [gender, setGender] = useState(null);
  const [verified, setVerified] = useState(!!cached?.verified);
  const [referralCount, setReferralCount] = useState(0);

  // Draft values used only while editing the profile itself
  const [draftUsername, setDraftUsername] = useState("");
  const [draftBio, setDraftBio] = useState("");
  const [draftGender, setDraftGender] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [bioEditMode, setBioEditMode] = useState(false);
  const [savingBio, setSavingBio] = useState(false);
  const [bioEditError, setBioEditError] = useState(null);
  const [nudgeDismissed, setNudgeDismissed] = useState(() => {
    try {
      return localStorage.getItem("virsa_profile_nudge_dismissed") === "1";
    } catch {
      return false;
    }
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const installOption = useInstallOption();
  const menuRef = useRef(null);
  const settingsMenuRef = useRef(null);
  useFocusTrap(settingsMenuRef, menuOpen);

  // The settings gear + dropdown render into the navbar's top bar (via
  // portal) instead of inline next to the username — look up that slot
  // once it's mounted.
  const [settingsSlot, setSettingsSlot] = useState(null);
  useEffect(() => {
    setSettingsSlot(document.getElementById("navbar-profile-settings-slot"));
  }, []);

  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Password reset (OTP-based, same pattern as the login page)
  const [resetStage, setResetStage] = useState(null); // null | "verify" | "otp" | "newpassword" | "done"
  const [resetOtp, setResetOtp] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [resetNotice, setResetNotice] = useState(null);
  const [resetCaptchaToken, setResetCaptchaToken] = useState(null);
  const resetTurnstileRef = useRef(null);
  // Cancelling the modal only sets resetStage back to null — it doesn't
  // cancel whatever auth.* call is still in flight. Without this, closing
  // the dialog mid-request used to let that request reopen it seconds
  // later, on a different stage, with a "we sent a code" notice the user
  // never asked for.
  const resetStageRef = useRef(resetStage);
  resetStageRef.current = resetStage;
  const resetModalRef = useRef(null);
  useFocusTrap(resetModalRef, !!resetStage);

  // Change email
  const [emailChangeStage, setEmailChangeStage] = useState(null); // null | "form" | "verify-old" | "verify-new" | "done"
  const [newEmail, setNewEmail] = useState("");
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [emailChangeError, setEmailChangeError] = useState(null);
  const [oldEmailOtp, setOldEmailOtp] = useState("");
  const [newEmailOtp, setNewEmailOtp] = useState("");
  // See resetStageRef above — same reason.
  const emailChangeStageRef = useRef(emailChangeStage);
  emailChangeStageRef.current = emailChangeStage;
  const emailChangeModalRef = useRef(null);
  useFocusTrap(emailChangeModalRef, !!emailChangeStage);

  // Verified badge request
  const [verificationStatus, setVerificationStatus] = useState(null); // null | "pending" | "approved" | "rejected"
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyReason, setVerifyReason] = useState("");
  const [verifySubmitting, setVerifySubmitting] = useState(false);
  const [verifyError, setVerifyError] = useState(null);
  const verifyModalRef = useRef(null);
  useFocusTrap(verifyModalRef, verifyModalOpen);

  // Delete account
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const deleteModalRef = useRef(null);
  useFocusTrap(deleteModalRef, deleteModalOpen);
  const [deleteError, setDeleteError] = useState(null);

  // Per-post edit/delete state
  const [openTileMenuId, setOpenTileMenuId] = useState(null);
  const [pendingDeleteIds, setPendingDeleteIds] = useState({}); // { [designId]: timeoutId }
  const pendingDeleteIdsRef = useRef(pendingDeleteIds);
  pendingDeleteIdsRef.current = pendingDeleteIds;
  // Always the latest closure of finalizeDeleteDesign (declared further down
  // — safe to reference here since it's a hoisted function declaration) —
  // called from the unmount effect below, which needs the freshest `designs`
  // snapshot for its Storage-cleanup lookup, not whatever was captured by
  // that effect's own one-time mount closure.
  const finalizeDeleteDesignRef = useRef(null);
  const [deleteToast, setDeleteToast] = useState(null); // { id, message } | null
  const [editingDesignId, setEditingDesignId] = useState(null);
  const [designEditCaption, setDesignEditCaption] = useState("");
  const [designEditError, setDesignEditError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const tileMenuRef = useRef(null);
  const pinningRef = useRef(false);
  // saveProfile/saveBioOnly/saveEditDesign only gated on saving/savingBio
  // state, same "only disables a button after the next render" gap this
  // file already treats as worth fixing everywhere else (OTP sends, push
  // toggle, upload) — a fast double-tap on Save could fire the RPC twice.
  const savingProfileRef = useRef(false);
  const savingBioRef = useRef(false);
  const editDesignSavingRef = useRef(false);
  const pushToggleBusyRef = useRef(false);
  const uploadDPBusyRef = useRef(false);
  const followersToggleBusyRef = useRef(false);
  const followingToggleBusyRef = useRef(false);
  // The 5s undo-delete timer (handleDeleteDesign below) is a plain
  // setTimeout, not tied to this component's lifecycle — if the page is
  // left before it fires, finalizeDeleteDesign still runs later against
  // this closure. The delete itself should still happen either way; this
  // just stops it from also touching state after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      // Without this, a post deleted just before navigating away (still
      // inside its 5s undo window) stays fully present in the DB when this
      // page is reopened before the timer fires — the new mount refetches
      // and shows it normally, then the OLD timer eventually fires the real
      // DELETE but (correctly) skips touching this new instance's state,
      // leaving a "deleted" post visibly stuck in the UI. Finalizing
      // immediately on unmount (same fix Comments.jsx already has for its
      // own soft-delete-with-undo flow) closes that gap.
      Object.entries(pendingDeleteIdsRef.current).forEach(([id, timer]) => {
        clearTimeout(timer);
        finalizeDeleteDesignRef.current?.(id);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Same pattern as Login.jsx's own once()/busyRef: `loading` state only
  // disables a submit button after the next render, so a fast double-tap or
  // double-Enter can fire a form's handler twice before that paints — two
  // OTP codes sent (the first becomes invalid), or a second verify of an
  // already-consumed code turning a real success into a false "didn't
  // work" error.
  //
  // Two SEPARATE refs, not one shared between the reset and email-change
  // modals: only one of those is ever visibly OPEN at a time, but closing
  // one doesn't cancel its in-flight request — a single shared ref meant
  // closing the reset modal mid-request and opening email-change instead
  // left the email-change submit silently doing nothing (no error, no
  // spinner) until the abandoned reset request finally settled.
  const resetBusyRef = useRef(false);
  const emailChangeBusyRef = useRef(false);
  function onceWith(busyRef, handler) {
    return async event => {
      if (busyRef.current) {
        event?.preventDefault?.();
        return;
      }
      busyRef.current = true;
      try {
        await handler(event);
      } finally {
        busyRef.current = false;
      }
    };
  }

  // Avatar crop-before-upload (object URL of the photo currently being cropped)
  const [avatarCropSrc, setAvatarCropSrc] = useState(null);
  const [avatarCropFileName, setAvatarCropFileName] = useState("photo.jpg");

  // Per-post comments toggle
  const [openComments, setOpenComments] = useState({});

  /* ================= LOAD USER ================= */
  useEffect(() => {
    async function getUser() {
      const {
        data: { session }
      } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    }
    getUser();
  }, []);

  /* ================= LOAD PROFILE ================= */
  useEffect(() => {
    if (!user) return;

    async function loadProfile() {
      let { data, error } = await supabase
        .from("users")
        .select("id, username, bio, avatar_url, verified, show_followers, show_following, referral_count")
        .eq("id", user.id)
        .single();

      // Each of these two extra columns landed in a separate migration —
      // referral_count is the newer of the two, so if only one is missing
      // (SQL run partway, or not yet at all) it's the one to drop first.
      // Falls all the way back to the original base columns so the profile
      // still loads even before any migration below this point has run.
      if (error?.code === "42703" || error?.code === "42501") {
        ({ data, error } = await supabase
          .from("users")
          .select("id, username, bio, avatar_url, verified, show_followers, show_following")
          .eq("id", user.id)
          .single());
      }
      if (error?.code === "42703" || error?.code === "42501") {
        ({ data, error } = await supabase
          .from("users")
          .select("id, username, bio, avatar_url, verified")
          .eq("id", user.id)
          .single());
      }

      if (error) {
        console.log("LOAD PROFILE ERROR:", error);
        setProfileLoaded(true);
        return;
      }

      // gender isn't selectable directly (like email, it's not grantable to
      // authenticated — nobody else should be able to read it via a plain
      // .select()), so it comes back through this instead: a SECURITY
      // DEFINER RPC that only ever returns the caller's own value.
      supabase.rpc("get_my_gender").then(({ data: g, error: genderError }) => {
        if (genderError) {
          console.log("LOAD GENDER ERROR:", genderError);
          return;
        }
        setGender(g || null);
      });

      setPhotoURL(data.avatar_url || "");
      setUsername(data.username || "");
      setBio(data.bio || "");
      setVerified(!!data.verified);
      setReferralCount(data.referral_count || 0);
      setShowFollowing(data.show_following !== false);
      setShowFollowers(data.show_followers !== false);
      setProfileLoaded(true);
    }

    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Keep the on-device copy in step with whatever the page is showing —
  // fresh fetches, edits and live count changes alike. A photo still being
  // uploaded is only a temporary blob: preview and isn't worth keeping.
  const userId = user?.id;
  useEffect(() => {
    if (!userId || !profileLoaded || !username || photoURL.startsWith("blob:")) return;
    // Signed out (or deleted) while this page is still on screen for a moment:
    // logout just wiped the cache, so don't put it back.
    if (getStoredUser()?.id !== userId) return;
    mergeProfileCache(userId, {
      username,
      avatar_url: photoURL,
      bio,
      verified,
      show_followers: showFollowers,
      show_following: showFollowing,
      posts_count: postsCount,
      follower_count: followerCount,
      following_count: followingCount,
      total_likes: totalLikes,
      total_downloads: totalDownloads
    });
  }, [
    userId, profileLoaded, username, photoURL, bio, verified, showFollowers, showFollowing,
    postsCount, followerCount, followingCount, totalLikes, totalDownloads
  ]);

  /* ================= VERIFICATION REQUEST STATUS ================= */
  useEffect(() => {
    if (!user) return;

    async function loadVerificationStatus() {
      const { data, error } = await supabase
        .from("verification_requests")
        .select("status")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.log("VERIFICATION STATUS ERROR:", error);
        return;
      }

      setVerificationStatus(data?.status || null);
    }

    loadVerificationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  /* ================= PUSH NOTIFICATION STATUS ================= */
  useEffect(() => {
    setPushSupported(isPushSupported());
    if (isPushSupported()) {
      isPushSubscribed().then(setPushEnabled);
    }
  }, []);

  function handleInstallApp() {
    setMenuOpen(false);
    startInstall(installOption);
  }

  async function togglePush() {
    if (pushToggleBusyRef.current) return;
    pushToggleBusyRef.current = true;
    setPushToggleError(null);

    // enablePush/disablePush call real browser APIs (pushManager.subscribe/
    // unsubscribe) that can throw rather than resolve to {error} — without
    // this try/finally, a thrown AbortError/NotAllowedError skipped the
    // reset below entirely and left the toggle permanently wedged until reload.
    try {
      if (pushEnabled) {
        const { error } = await disablePush();
        if (error) {
          setPushToggleError("Couldn't turn off push notifications.");
          return;
        }
        setPushEnabled(false);
      } else {
        const { error } = await enablePush(user.id);
        if (error) {
          setPushToggleError(error.message || "Couldn't enable push notifications.");
          return;
        }
        setPushEnabled(true);
      }
    } finally {
      pushToggleBusyRef.current = false;
    }
  }

  /* ================= LOAD FOLLOWER / FOLLOWING COUNTS ================= */
  useEffect(() => {
    if (!user) return;

    async function loadCounts() {
      const [{ count: followers, error: followersError }, { count: following, error: followingError }] = await Promise.all([
        supabase
          .from("follows")
          .select("*", { count: "exact", head: true })
          .eq("following_id", user.id),
        supabase
          .from("follows")
          .select("*", { count: "exact", head: true })
          .eq("follower_id", user.id),
      ]);

      // A failed fetch has no count — keep whatever is showing (the copy kept
      // on this device) instead of overwriting it, and the cache, with 0.
      if (!followersError) setFollowerCount(followers || 0);
      if (!followingError) setFollowingCount(following || 0);
    }

    loadCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Live updates: follows/unfollows made by ANYONE, plus this viewer's own
  // likes (another tab/device on the same account) — merged onto one
  // channel instead of two, since both are keyed to this same user and
  // share the same mount/unmount lifecycle (see utils/myEngagement.js).
  useEffect(() => {
    if (!user) return undefined;

    const channel = attachMyEngagementBindings(supabase.channel(`profile-live-${user.id}`), user.id, {
      onLike: id => setDesigns(prev => prev.map(d => (d.id === id ? { ...d, _likedByMe: true } : d))),
      onUnlike: id => setDesigns(prev => prev.map(d => (d.id === id ? { ...d, _likedByMe: false } : d)))
    });

    channel
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "follows", filter: `following_id=eq.${user.id}` },
        () => setFollowerCount(prev => prev + 1)
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "follows", filter: `following_id=eq.${user.id}` },
        () => setFollowerCount(prev => Math.max(0, prev - 1))
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "follows", filter: `follower_id=eq.${user.id}` },
        () => setFollowingCount(prev => prev + 1)
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "follows", filter: `follower_id=eq.${user.id}` },
        () => setFollowingCount(prev => Math.max(0, prev - 1))
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  /* ================= FETCH MY DESIGNS (+ comment counts) ================= */
  useEffect(() => {
    if (!user) return;
    activeSortRef.current = sortBy;
    fetchMyDesigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, sortBy]);

  // Keeps the latest `designs` for the poll below to read without a stale
  // closure — it runs on its own timer, not in response to designs changing.
  const designsRef = useRef(designs);
  useEffect(() => {
    designsRef.current = designs;
  }, [designs]);

  // Refreshes likes/downloads/comment_count for whatever's currently loaded
  // on this page, every 30s — not a realtime listener, since a plain
  // "designs" UPDATE subscription has no way to scope itself to just the
  // posts on screen and would broadcast every design's change to every
  // connected client regardless (see utils/statsPolling.js). Whether *this
  // viewer* liked each one is a separate, per-user concern — see the
  // myEngagement effect below.
  useEffect(() => {
    return subscribeToStatsPolling(
      () => designsRef.current.map(d => d.id),
      rows => {
        const byId = new Map(rows.map(r => [r.id, r]));
        setDesigns(prev =>
          prev.map(d => {
            const fresh = byId.get(d.id);
            return fresh
              ? { ...d, likes: fresh.likes, downloads: fresh.downloads, comment_count: fresh.comment_count }
              : d;
          })
        );
      }
    );
  }, []);


  // Lightweight aggregate query (just the two counter columns) so the
  // "likes / downloads" totals reflect ALL posts even though the grid
  // below only loads PAGE_SIZE at a time.
  // Aggregated in Postgres (get_user_stats) instead of fetching every one
  // of your posts to sum likes/downloads client-side — stays a single-row
  // read no matter how many designs you've posted.
  async function fetchStats() {
    const { data, error } = await supabase.rpc("get_user_stats", {
      target_user_id: user.id
    });

    if (error) {
      console.log("STATS ERROR:", error);
      return;
    }

    const row = data?.[0];
    setTotalLikes(row?.total_likes || 0);
    setTotalDownloads(row?.total_downloads || 0);
    setPostsCount(row?.post_count || 0);
  }

  // A pinned post always sorts first, regardless of which secondary sort
  // is chosen — same idea as Instagram/Twitter's single pinned-post slot.
  function applySort(query) {
    query = query.order("pinned", { ascending: false });
    if (sortBy === "oldest") return query.order("created_at", { ascending: true });
    if (sortBy === "likes") return query.order("likes", { ascending: false });
    return query.order("created_at", { ascending: false });
  }

  async function fetchMyDesigns() {
    const requestSort = sortBy;
    setPostsLoading(true);

    const { data, error } = await applySort(
      supabase.from("designs").select("*").eq("user_id", user.id)
    ).range(0, PAGE_SIZE - 1);

    // Changing the sort dropdown quickly enough for two of these to overlap
    // used to let whichever resolved last win, even if it was for a sort
    // option no longer selected.
    if (requestSort !== activeSortRef.current) return;

    if (error) {
      console.log(error);
      setPostsLoading(false);
      return;
    }

    let myDesigns = data || [];
    const { likedIds } = await fetchMyEngagement(myDesigns.map(d => d.id), user.id);
    myDesigns = myDesigns.map(d => ({ ...d, _likedByMe: likedIds.has(d.id) }));

    setDesigns(myDesigns);
    setPostsOffset(PAGE_SIZE);
    setHasMorePosts(myDesigns.length === PAGE_SIZE);
    setPostsLoading(false);

    fetchStats();
  }

  // Load the next PAGE_SIZE of your own posts (infinite scroll).
  async function loadMorePosts() {
    if (postsLoading || loadingMorePosts || !hasMorePosts) return;
    const requestSort = sortBy;
    setLoadingMorePosts(true);

    const { data, error } = await applySort(
      supabase.from("designs").select("*").eq("user_id", user.id)
    ).range(postsOffset, postsOffset + PAGE_SIZE - 1);

    // The sort changed (and fetchMyDesigns already reset the list) while
    // this page was still in flight — appending it now would tack the OLD
    // sort's posts onto the NEW sort's freshly-reset list. Still have to
    // clear loadingMorePosts here too: left stuck true, every future
    // loadMorePosts() call for the new sort would return immediately (its
    // own guard checks loadingMorePosts), permanently breaking infinite
    // scroll until the page reloads.
    if (requestSort !== activeSortRef.current) {
      setLoadingMorePosts(false);
      return;
    }

    if (error) {
      console.log("LOAD MORE POSTS ERROR:", error);
      setLoadingMorePosts(false);
      return;
    }

    let newDesigns = data || [];
    const { likedIds } = await fetchMyEngagement(newDesigns.map(d => d.id), user.id);
    newDesigns = newDesigns.map(d => ({ ...d, _likedByMe: likedIds.has(d.id) }));

    setDesigns(prev => [...prev, ...newDesigns]);
    setPostsOffset(prev => prev + PAGE_SIZE);
    setHasMorePosts(newDesigns.length === PAGE_SIZE);
    setLoadingMorePosts(false);
  }

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || postsLoading || !hasMorePosts) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) loadMorePosts();
      },
      { rootMargin: "400px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postsLoading, postsOffset, hasMorePosts, loadingMorePosts]);

  /* ================= CLOSE MENUS ON OUTSIDE CLICK ================= */
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
      if (tileMenuRef.current && !tileMenuRef.current.contains(e.target)) {
        setOpenTileMenuId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // On mobile the settings menu becomes a fixed panel over the page (see
  // .profile-settings-menu) rather than a small anchored dropdown — without
  // this, the page underneath still scrolls while it's open, which feels
  // broken since the menu itself doesn't move with it.
  useEffect(() => {
    if (!menuOpen) return;
    const unlockScroll = lockBodyScroll();
    return () => {
      unlockScroll();
    };
  }, [menuOpen]);

  /* ================= UPLOAD DP ================= */
  async function uploadDP(file) {
    if (!file || !user) return;
    // Nothing stopped a second upload (pick → crop → save, again) from
    // starting while the first was still awaiting Storage/the RPC — each
    // call closes over its own previousPhotoURL, so if the two responses
    // arrived out of order the slower one's success handler could overwrite
    // photoURL/the DB's avatar_url with a stale value after the newer
    // upload had already "won".
    if (uploadDPBusyRef.current) return;
    uploadDPBusyRef.current = true;

    try {
      const previousPhotoURL = photoURL;
      const previewUrl = URL.createObjectURL(file);
      setPhotoURL(previewUrl);
      setUploading(true);
      setError(null);

      const fileName = `${user.id}-${Date.now()}`;

      const { error: uploadError } = await supabase.storage
        .from("profiles")
        .upload(fileName, file, { cacheControl: "31536000" });

      if (uploadError) {
        console.log(uploadError);
        setError(friendlyError(uploadError, "Couldn't upload that photo. Try again."));
        setPhotoURL(previousPhotoURL);
        URL.revokeObjectURL(previewUrl);
        setUploading(false);
        return;
      }

      const {
        data: { publicUrl }
      } = supabase.storage.from("profiles").getPublicUrl(fileName);

      const { error: updateError } = await supabase.rpc("update_own_profile", { new_avatar_url: publicUrl });

      if (updateError) {
        console.log(updateError);
        setError("Photo uploaded, but couldn't save it to your profile.");
        setPhotoURL(previousPhotoURL);
        URL.revokeObjectURL(previewUrl);
        setUploading(false);
        return;
      }

      setPhotoURL(publicUrl);
      URL.revokeObjectURL(previewUrl);
      setUploading(false);

      // Best-effort: now that the profile points at the new photo, remove the
      // file the old one pointed to — not awaited, so a slow/failed cleanup
      // here never blocks or affects the (already successful) upload.
      const oldPath = storagePathFromPublicUrl(previousPhotoURL, "profiles");
      if (oldPath) {
        removeStoragePaths("profiles", [oldPath]).catch(err => console.log("OLD AVATAR CLEANUP ERROR:", err));
      }
    } finally {
      uploadDPBusyRef.current = false;
    }
  }

  function pickAvatarFile(file) {
    if (!file) return;
    setAvatarCropFileName(file.name || "photo.jpg");
    setAvatarCropSrc(URL.createObjectURL(file));
  }

  function closeAvatarCrop() {
    if (avatarCropSrc) URL.revokeObjectURL(avatarCropSrc);
    setAvatarCropSrc(null);
  }

  function handleAvatarCropSave(croppedFile) {
    closeAvatarCrop();
    uploadDP(croppedFile);
  }

  /* ================= EDIT PROFILE (username/bio) ================= */
  function startEdit() {
    setDraftUsername(username);
    setDraftBio(bio);
    setDraftGender(gender);
    setError(null);
    setNotice(null);
    setEditMode(true);
    setBioEditMode(false);
    setMenuOpen(false);
  }

  function cancelEdit() {
    setEditMode(false);
    setError(null);
  }

  // A quick path to change just the bio — skips username validation and the
  // uniqueness check entirely, so someone who only wants to update their
  // bio doesn't have to touch (or wait on a network round-trip for) a field
  // they never meant to change.
  function startBioEdit() {
    setDraftBio(bio);
    setBioEditError(null);
    setBioEditMode(true);
  }

  function cancelBioEdit() {
    setBioEditMode(false);
    setBioEditError(null);
  }

  async function saveBioOnly() {
    if (savingBioRef.current) return;
    savingBioRef.current = true;
    setBioEditError(null);
    setSavingBio(true);

    try {
      const { error } = await supabase.rpc("update_own_profile", { new_bio: draftBio });

      if (error) {
        console.log("SAVE BIO ERROR:", error);
        setBioEditError(friendlyError(error, "Couldn't save. Try again."));
        return;
      }

      setBio(draftBio);
      setBioEditMode(false);
    } finally {
      setSavingBio(false);
      savingBioRef.current = false;
    }
  }

  const BIO_CHAR_LIMIT = 120;

  // Just pass the value through — the textarea's own maxLength already stops
  // new typing past the limit, without blocking backspace/deletion on a
  // bio that was already longer than this before the limit existed.
  function handleBioChange(value) {
    setDraftBio(value);
  }

  async function saveProfile() {
    if (!user) return;
    if (savingProfileRef.current) return;
    setError(null);
    setNotice(null);

    const cleanUsername = draftUsername.trim().toLowerCase();

    if (!cleanUsername) {
      setError("Username is required.");
      return;
    }
    if (!USERNAME_REGEX.test(cleanUsername)) {
      setError('Username: 3–20 chars, lowercase letters, numbers, "." or "_" only.');
      return;
    }

    savingProfileRef.current = true;
    setSaving(true);

    try {
      const { data: existingUser, error: checkError } = await supabase
        .from("users")
        .select("id")
        .eq("username", cleanUsername)
        .neq("id", user.id)
        .maybeSingle();

      if (checkError) {
        console.log("USERNAME CHECK ERROR:", checkError);
        setError("Couldn't verify that username right now. Try again.");
        return;
      }

      if (existingUser) {
        setError("That username is already taken.");
        return;
      }

      // Via RPC, not a direct table UPDATE — this table has a Supabase-managed
      // Data API restriction that blocks the plain PATCH endpoint even when
      // RLS and column grants are correct (same issue the show_following
      // toggle hit). Unique-username violations still surface as a normal
      // 23505 from the underlying UPDATE inside the function.
      const { error: saveError } = await supabase.rpc("update_own_profile", {
        new_username: cleanUsername,
        new_bio: draftBio,
        new_avatar_url: photoURL || "",
        new_gender: draftGender
      });

      if (saveError) {
        console.log(saveError);
        if (saveError.code === "23505") {
          setError("That username was just taken — try another.");
          return;
        }
        setError(friendlyError(saveError, "Couldn't save your profile. Try again."));
        return;
      }

      setUsername(cleanUsername);
      setBio(draftBio);
      setGender(draftGender);
      setEditMode(false);
      setNotice("Profile updated.");
    } finally {
      setSaving(false);
      savingProfileRef.current = false;
    }
  }

  /* ================= PASSWORD RESET (OTP, same pattern as login) ================= */
  // Goes through an RPC (SECURITY DEFINER, WHERE id = auth.uid()) instead of
  // a direct table UPDATE — this table has a Supabase-managed Data API
  // restriction that blocks the plain PATCH endpoint even when RLS and
  // column grants are both correct, which an RPC call isn't subject to.
  async function toggleShowFollowers() {
    if (followersToggleBusyRef.current) return;
    followersToggleBusyRef.current = true;
    const next = !showFollowers;
    setShowFollowers(next);
    setFollowersToggleError(null);

    const { error } = await supabase.rpc("update_own_visibility", { new_show_followers: next });
    followersToggleBusyRef.current = false;

    if (error) {
      console.log("TOGGLE SHOW_FOLLOWERS ERROR:", error);
      setShowFollowers(!next);
      setFollowersToggleError("Couldn't save. Try again.");
    }
  }

  async function toggleShowFollowing() {
    if (followingToggleBusyRef.current) return;
    followingToggleBusyRef.current = true;
    const next = !showFollowing;
    setShowFollowing(next);
    setFollowingToggleError(null);

    const { error } = await supabase.rpc("update_own_visibility", { new_show_following: next });
    followingToggleBusyRef.current = false;

    if (error) {
      console.log("TOGGLE SHOW_FOLLOWING ERROR:", error);
      setShowFollowing(!next);
      setFollowingToggleError("Couldn't save. Try again.");
    }
  }

  function openPasswordReset() {
    setMenuOpen(false);
    setResetStage("verify");
    setResetOtp("");
    setResetNewPassword("");
    setResetError(null);
    setResetNotice(null);
    setResetCaptchaToken(null);
  }

  // Called once the Turnstile widget on the "verify" stage passes (usually
  // near-instant) — moves straight to sending the code so opening this menu
  // item still feels like one step, not two.
  function handleResetCaptchaVerify(token) {
    setResetCaptchaToken(token);
    sendResetOtp(token);
  }

  async function sendResetOtp(token) {
    // Guards against Turnstile's own auto-refresh: its default
    // refresh-expired:"auto" behavior can re-invoke onVerify (and so
    // handleResetCaptchaVerify -> here) with a fresh token while this
    // call is still in flight from the first one, firing two concurrent
    // signInWithOtp requests for the same account.
    if (resetBusyRef.current) return;
    resetBusyRef.current = true;

    try {
      setResetLoading(true);
      setResetError(null);

      let error;
      try {
        ({ error } = await withTimeout(
          supabase.auth.signInWithOtp({
            email: user.email,
            options: { shouldCreateUser: false, captchaToken: token }
          }),
          15000
        ));
      } catch (timeoutErr) {
        setResetLoading(false);
        setResetCaptchaToken(null);
        resetTurnstileRef.current?.reset();
        setResetError("Couldn't send the code right now. Try again.");
        return;
      }

      setResetLoading(false);
      // Tokens are single-use — clear it and get a fresh one ready for a
      // possible resend, whether this attempt succeeded or not.
      setResetCaptchaToken(null);
      resetTurnstileRef.current?.reset();

      if (error) {
        setResetError(friendlyError(error, "Couldn't send the code. Try again."));
        return;
      }

      // The user closed the modal while this was in flight — don't reopen
      // it on a stage they never asked for.
      if (resetStageRef.current === null) return;

      setResetStage("otp");
      setResetNotice(`We sent a 6-digit code to ${user.email}.`);
    } finally {
      resetBusyRef.current = false;
    }
  }

  async function verifyResetOtp(e) {
    e.preventDefault();
    setResetError(null);

    if (resetOtp.trim().length < 6) {
      setResetError("Enter the 6-digit code from your email.");
      return;
    }

    setResetLoading(true);
    let error;
    try {
      ({ error } = await withTimeout(
        supabase.auth.verifyOtp({
          email: user.email,
          token: resetOtp.trim(),
          type: "email"
        }),
        8000
      ));
    } catch (timeoutErr) {
      setResetLoading(false);
      setResetError("Couldn't verify that code right now. Try again.");
      return;
    }
    setResetLoading(false);

    if (error) {
      setResetError("That code didn't work. Check it and try again.");
      return;
    }

    if (resetStageRef.current === null) return;
    setResetStage("newpassword");
  }

  async function submitResetPassword(e) {
    e.preventDefault();
    setResetError(null);

    if (!isPasswordValid(resetNewPassword)) {
      setResetError(PASSWORD_REQUIREMENTS_TEXT);
      return;
    }

    setResetLoading(true);
    let error;
    try {
      ({ error } = await withTimeout(supabase.auth.updateUser({ password: resetNewPassword }), 8000));
    } catch (timeoutErr) {
      setResetLoading(false);
      setResetError("Couldn't update your password right now. Try again.");
      return;
    }
    setResetLoading(false);

    if (error) {
      setResetError(friendlyError(error, "Couldn't update your password. Try again."));
      return;
    }

    if (resetStageRef.current === null) return;
    setResetStage("done");
  }

  function closeResetModal() {
    // Closing didn't cancel whatever auth.* call sendResetOtp/etc. still had
    // in flight (see resetStageRef above), which was deliberate so an
    // abandoned request can't silently reopen this modal later — but that
    // left resetBusyRef still set until that request settled, so reopening
    // and immediately trying again (a fresh Turnstile pass, a fresh submit)
    // used to silently no-op against the OLD request's still-held guard.
    // Refusing to close at all while a request is genuinely in flight
    // avoids ever creating that abandoned-request window in the first place.
    if (resetLoading) return;
    setResetStage(null);
  }

  function openEmailChange() {
    setMenuOpen(false);
    setEmailChangeStage("form");
    setNewEmail("");
    setOldEmailOtp("");
    setNewEmailOtp("");
    setEmailChangeError(null);
  }

  function closeEmailChangeModal() {
    // See closeResetModal's comment above — same reason.
    if (emailChangeLoading) return;
    setEmailChangeStage(null);
  }

  async function submitEmailChange(e) {
    e.preventDefault();
    setEmailChangeError(null);

    const clean = newEmail.trim().toLowerCase();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clean)) {
      setEmailChangeError("Enter a valid email address.");
      return;
    }

    if (clean === user.email) {
      setEmailChangeError("That's already your current email.");
      return;
    }

    setEmailChangeLoading(true);
    let error;
    try {
      ({ error } = await withTimeout(supabase.auth.updateUser({ email: clean }), 8000));
    } catch (timeoutErr) {
      setEmailChangeLoading(false);
      setEmailChangeError("Couldn't start the email change right now. Try again.");
      return;
    }
    setEmailChangeLoading(false);

    if (error) {
      setEmailChangeError(friendlyError(error, "Couldn't start the email change. Try again."));
      return;
    }

    if (emailChangeStageRef.current === null) return;
    setNewEmail(clean);
    setOldEmailOtp("");
    setNewEmailOtp("");
    setEmailChangeStage("verify-old");
  }

  // Secure email change is on for this project (Supabase Dashboard → Auth →
  // Sign In / Providers → Email), so a change needs a code confirmed from
  // BOTH the current email and the new one before it actually takes effect —
  // this mirrors that with two OTP steps instead of two clicked links.
  async function verifyOldEmailOtp(e) {
    e.preventDefault();
    setEmailChangeError(null);

    if (oldEmailOtp.trim().length < 6) {
      setEmailChangeError("Enter the 6-digit code from your current email.");
      return;
    }

    setEmailChangeLoading(true);
    let error;
    try {
      ({ error } = await withTimeout(
        supabase.auth.verifyOtp({
          email: user.email,
          token: oldEmailOtp.trim(),
          type: "email_change"
        }),
        8000
      ));
    } catch (timeoutErr) {
      setEmailChangeLoading(false);
      setEmailChangeError("Couldn't verify that code right now. Try again.");
      return;
    }
    setEmailChangeLoading(false);

    if (error) {
      setEmailChangeError("That code didn't work. Check it and try again.");
      return;
    }

    if (emailChangeStageRef.current === null) return;
    setEmailChangeStage("verify-new");
  }

  async function verifyNewEmailOtp(e) {
    e.preventDefault();
    setEmailChangeError(null);

    if (newEmailOtp.trim().length < 6) {
      setEmailChangeError("Enter the 6-digit code from your new email.");
      return;
    }

    setEmailChangeLoading(true);
    let error;
    try {
      ({ error } = await withTimeout(
        supabase.auth.verifyOtp({
          email: newEmail,
          token: newEmailOtp.trim(),
          type: "email_change"
        }),
        8000
      ));
    } catch (timeoutErr) {
      setEmailChangeLoading(false);
      setEmailChangeError("Couldn't verify that code right now. Try again.");
      return;
    }
    setEmailChangeLoading(false);

    if (error) {
      // A first attempt that timed out client-side may still have
      // succeeded — the retry then fails because that code is already
      // used. Unlike the reset-password OTP (which doesn't change anything
      // checkable), a successful email change DOES change the signed-in
      // session's email, so that's checkable here.
      const {
        data: { session: retrySession }
      } = await supabase.auth.getSession();
      if (retrySession?.user?.email?.toLowerCase() === newEmail.trim().toLowerCase()) {
        setUser(retrySession.user);
        if (emailChangeStageRef.current === null) return;
        setEmailChangeStage("done");
        return;
      }
      setEmailChangeError("That code didn't work. Check it and try again.");
      return;
    }

    const {
      data: { session }
    } = await supabase.auth.getSession();
    setUser(session?.user ?? null);

    if (emailChangeStageRef.current === null) return;
    setEmailChangeStage("done");
  }

  async function resendEmailChangeCodes() {
    setEmailChangeError(null);
    setEmailChangeLoading(true);
    let error;
    try {
      ({ error } = await withTimeout(supabase.auth.updateUser({ email: newEmail }), 8000));
    } catch (timeoutErr) {
      setEmailChangeLoading(false);
      setEmailChangeError("Couldn't resend the codes right now. Try again.");
      return;
    }
    setEmailChangeLoading(false);

    if (error) {
      setEmailChangeError(friendlyError(error, "Couldn't resend the codes. Try again."));
      return;
    }

    if (emailChangeStageRef.current === null) return;
    setOldEmailOtp("");
    setNewEmailOtp("");
    setEmailChangeStage("verify-old");
  }

  function openVerifyModal() {
    setMenuOpen(false);
    setVerifyModalOpen(true);
    setVerifyReason("");
    setVerifyError(null);
  }

  function closeVerifyModal() {
    setVerifyModalOpen(false);
  }

  async function submitVerificationRequest(e) {
    e.preventDefault();
    setVerifyError(null);

    if (!verifyReason.trim()) {
      setVerifyError("Tell us a bit about why you'd like to be verified.");
      return;
    }

    setVerifySubmitting(true);

    const { error } = await supabase.from("verification_requests").insert({
      user_id: user.id,
      reason: verifyReason.trim()
    });

    setVerifySubmitting(false);

    if (error) {
      console.log("VERIFICATION REQUEST ERROR:", error);
      setVerifyError(friendlyError(error, "Couldn't submit your request. Try again."));
      return;
    }

    setVerificationStatus("pending");
    setVerifyModalOpen(false);
  }

  function openDeleteModal() {
    setMenuOpen(false);
    setDeleteModalOpen(true);
    setDeleteConfirmText("");
    setDeleteError(null);
  }

  function closeDeleteModal() {
    if (deleteLoading) return;
    setDeleteModalOpen(false);
  }

  // Escape closes whichever of these modals happens to be open. Relies on
  // these 4 states being mutually exclusive with an open caption-editor
  // HashtagInput elsewhere on this page — that field's own Escape handler
  // calls stopPropagation() (to dismiss just its suggestion dropdown without
  // also closing a parent sheet), so if one of these modals ever became
  // reachable at the same time as an open caption editor, Escape typed into
  // the editor would stop reaching this listener.
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key !== "Escape") return;
      if (resetStage) closeResetModal();
      else if (emailChangeStage) closeEmailChangeModal();
      else if (verifyModalOpen) closeVerifyModal();
      else if (deleteModalOpen) closeDeleteModal();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetStage, emailChangeStage, verifyModalOpen, deleteModalOpen, deleteLoading]);

  // Deleting the database rows doesn't remove the actual files from
  // Storage — do that first, while the design rows (and their file URLs)
  // still exist to read from. Runs server-side (an Edge Function, with the
  // service role) instead of looping through Storage deletes from here
  // directly, so it still finishes even if this tab closes or the
  // connection drops partway through — that used to leave orphaned files
  // behind with no surviving DB row left to ever find them by. Best-effort
  // either way: a storage hiccup shouldn't block the account deletion itself.
  async function cleanupOwnStorage() {
    const { error } = await supabase.functions.invoke("delete-account-storage");
    if (error) {
      console.log("STORAGE CLEANUP ERROR:", error);
    }
  }

  async function confirmDeleteAccount() {
    if (deleteConfirmText.trim().toUpperCase() !== "DELETE") {
      setDeleteError('Type "DELETE" to confirm.');
      return;
    }

    setDeleteError(null);
    setDeleteLoading(true);

    await cleanupOwnStorage();

    const { error } = await supabase.rpc("delete_own_account");

    if (error) {
      console.log("DELETE ACCOUNT ERROR:", error);
      setDeleteError(friendlyError(error, "Couldn't delete your account. Try again."));
      setDeleteLoading(false);
      return;
    }

    await supabase.auth.signOut();
    clearLocalUserData();
    disablePush();
    navigate("/");
  }

  /* ================= EDIT / DELETE A DESIGN ================= */
  function startEditDesign(design) {
    setEditingDesignId(design.id);
    setDesignEditCaption(buildLegacyCaption(design));
    setDesignEditError(null);
    setOpenTileMenuId(null);
  }

  function cancelEditDesign() {
    setEditingDesignId(null);
    setDesignEditError(null);
  }

  async function saveEditDesign(id) {
    if (editDesignSavingRef.current) return;
    editDesignSavingRef.current = true;
    try {
      await saveEditDesignInner(id);
    } finally {
      editDesignSavingRef.current = false;
    }
  }

  async function saveEditDesignInner(id) {
    // The textarea's maxLength only blocks further TYPING past the limit —
    // it doesn't retroactively trim a value set programmatically, and
    // startEditDesign populates this from buildLegacyCaption, which can
    // produce a longer string than TITLE_LIMIT for an old post whose
    // separate title+description together once exceeded it. Without this,
    // saving straight after opening the editor (no typing at all) could
    // silently persist a title past the limit every other post enforces
    // (same fix as Home.jsx's saveEdit).
    const title = designEditCaption.trim().slice(0, TITLE_LIMIT);
    const hashtags = extractHashtags(title);

    // startEditDesign builds designEditCaption via buildLegacyCaption, which
    // folds a pre-merge post's separate `description` into the caption text
    // being edited here — but saving only ever wrote title/hashtags back,
    // leaving the old `description` column untouched. The card renders
    // title AND (separately) description when present, so a legacy post
    // ended up showing its own description twice after being edited once.
    // Clearing it here completes the one-time upgrade buildLegacyCaption
    // started (same fix as Home.jsx's saveEdit).
    const { error } = await supabase
      .from("designs")
      .update({ title, hashtags, description: null })
      .eq("id", id);

    if (error) {
      console.log(error);
      setDesignEditError(friendlyError(error, "Couldn't save. Try again."));
      return;
    }

    setDesigns(prev => prev.map(d => (d.id === id ? { ...d, title, hashtags, description: null } : d)));
    setEditingDesignId(null);
  }

  finalizeDeleteDesignRef.current = finalizeDeleteDesign;

  // Soft-delete: the post is hidden right away (filtered out of the grid
  // below via pendingDeleteIds) and only actually removed from the database
  // once the undo toast expires without being clicked.
  function handleDeleteDesign(id) {
    setOpenTileMenuId(null);

    const timer = setTimeout(() => finalizeDeleteDesign(id), 5000);
    setPendingDeleteIds(prev => ({ ...prev, [id]: timer }));
    setDeleteToast({ id, message: "Post deleted" });
  }

  async function finalizeDeleteDesign(id) {
    if (mountedRef.current) {
      setPendingDeleteIds(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }

    // Read from local state before the row is gone — it already has this design's
    // photo/file references, so no extra fetch is needed to know what to clean up.
    const design = designs.find(d => d.id === id);

    // The actual delete always runs, even if the page was left before this
    // fired — only the UI updates below are skipped after unmount.
    const { error } = await supabase.from("designs").delete().eq("id", id);

    if (error) {
      console.log("DELETE DESIGN ERROR:", error);
      return;
    }

    if (mountedRef.current) setDesigns(prev => prev.filter(d => d.id !== id));

    // Deleting the row doesn't remove the actual files from Storage — best-effort,
    // and not awaited, so a slow Storage response never delays the UI update above.
    if (design) {
      removeDesignStorage(design).catch(err => console.log("DESIGN STORAGE CLEANUP ERROR:", err));
    }
  }

  function undoDeleteDesign(id) {
    const timer = pendingDeleteIds[id];
    if (timer) clearTimeout(timer);

    setPendingDeleteIds(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setDeleteToast(null);
  }

  // Only one pinned post at a time — unpin whichever one currently holds
  // the spot before pinning a new one, then reload so pin+sort order stays
  // correct without needing to reorder anything by hand.
  async function togglePinDesign(design) {
    // This is two sequential awaited writes (unpin whoever currently holds
    // the spot, then pin this one) — a second click landing while the first
    // is still between those two steps could unpin-then-pin its own target
    // too, and depending on how the two overlap, both designs could end up
    // pinned=true at once, breaking the "only one pinned post" invariant
    // this function exists to maintain.
    if (pinningRef.current) return;
    pinningRef.current = true;

    setOpenTileMenuId(null);
    const nowPinning = !design.pinned;

    if (nowPinning) {
      await supabase
        .from("designs")
        .update({ pinned: false })
        .eq("user_id", user.id)
        .eq("pinned", true);
    }

    const { error } = await supabase
      .from("designs")
      .update({ pinned: nowPinning })
      .eq("id", design.id);

    pinningRef.current = false;

    if (error) {
      console.log("TOGGLE PIN ERROR:", error);
      return;
    }

    fetchMyDesigns();
  }

  /* ================= COMMENTS TOGGLE ================= */
  async function refreshCommentCount(designId) {
    const { count, error } = await supabase
      .from("comments")
      .select("id", { count: "exact", head: true })
      .eq("design_id", designId);

    if (error) {
      console.log("REFRESH COUNT ERROR:", error);
      return;
    }

    setDesigns(prev =>
      prev.map(d =>
        d.id === designId ? { ...d, comment_count: count ?? d.comment_count } : d
      )
    );
  }

  function toggleComments(id) {
    const isCurrentlyOpen = !!openComments[id];
    setOpenComments(prev => ({ ...prev, [id]: !isCurrentlyOpen }));

    if (isCurrentlyOpen) {
      refreshCommentCount(id);
    }
  }

  if (!user) {
    return (
      <p style={{ padding: 20 }}>
        <IoLockClosedOutline style={{ verticalAlign: -2, marginRight: 6 }} />
        Please log in
      </p>
    );
  }

  const visiblePosts = designs.filter(d => !pendingDeleteIds[d.id]);

  return (
    <div className="profile-page" style={page}>
      <style>{`
        .virsa-input:focus { border-color: var(--accent-primary) !important; }
        .virsa-otp { letter-spacing: 8px; font-size: 20px; text-align: center; font-weight: 700; }

        /* ===== Edit profile form ===== */
        .pf-field {
          margin-top: 10px;
          padding: 9px 14px 8px;
          background: var(--bg-soft);
          border: 1.5px solid transparent;
          border-radius: 14px;
          transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
        }
        .pf-field:focus-within {
          background: var(--bg-surface);
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px rgba(13, 59, 122, 0.12);
        }
        .pf-field label, .pf-cap {
          display: block;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--text-muted);
        }
        .pf-inline { display: flex; align-items: center; gap: 4px; }
        .pf-at { font-size: 16px; font-weight: 600; color: var(--text-muted); }
        .pf-field input, .pf-field textarea {
          width: 100%;
          box-sizing: border-box;
          padding: 4px 0 2px;
          border: none;
          outline: none;
          background: transparent;
          color: var(--text-heading);
          font: inherit;
          font-size: 16px; /* 16px so iPhones don't zoom the page on focus */
        }
        .pf-field textarea { display: block; min-height: 76px; resize: none; line-height: 1.45; }
        .pf-field input::placeholder, .pf-field textarea::placeholder { color: var(--text-muted); }
        .pf-count { text-align: right; font-size: 11.5px; color: var(--text-muted); }
        .pf-count.warn { color: var(--accent-danger); }
        .pf-hint { margin: 6px 4px 0; font-size: 12px; color: var(--text-muted); }
        .pf-group { margin-top: 14px; }
        .pf-group .pf-cap { margin: 0 4px 6px; }
        .pf-actions { display: flex; gap: 10px; }
        .pf-actions button {
          box-sizing: border-box;
          height: 48px;
          border-radius: 14px;
          font: inherit;
          font-size: 15px;
          cursor: pointer;
        }
        .pf-cancel { flex: 1; background: transparent; border: 1.5px solid var(--border-input); color: var(--text-secondary); font-weight: 600; }
        .pf-save { flex: 2; background: var(--accent-primary); border: 1.5px solid var(--accent-primary); color: var(--accent-contrast); font-weight: 700; }
        .pf-actions button:disabled { opacity: 0.65; cursor: default; }

        /* ===== Mobile layout ===== */
        @media (max-width: 640px) {
          .profile-page { padding: 4px !important; }
          .profile-header {
            padding: 16px !important;
          }
          .profile-header-row {
            gap: 16px !important;
          }
          .profile-avatar { width: 72px !important; height: 72px !important; }
          .profile-stats-row { gap: 18px !important; }
          .profile-stats-row .profile-stat-value { font-size: 18px !important; }
          .profile-secondary-stats { flex-wrap: wrap !important; }
          .profile-grid {
            grid-template-columns: 1fr !important;
          }
          .profile-settings-menu {
            position: fixed !important;
            top: auto !important;
            right: 12px !important;
            left: 12px !important;
            bottom: calc(90px + env(safe-area-inset-bottom)) !important;
            min-width: 0 !important;
            width: auto !important;
            max-height: 65vh !important;
            overflow-y: auto !important;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
          }
        }

        /* ===== Desktop layout ===== */
        @media (min-width: 641px) {
          .profile-grid {
            grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)) !important;
          }
        }
      `}</style>

      {/* ===== PROFILE HEADER ===== */}
      <div className="profile-header" style={header}>
       <div className="profile-header-row" style={topRow}>
        <div style={avatarWrap}>
          <div style={{ position: "relative", display: "block" }}>
            <img
              src={photoURL || "/default-avatar.svg"}
              alt={`${username || "Your"} avatar`}
              className="profile-avatar"
              style={{ ...avatar, cursor: "pointer" }}
              onClick={() => photoURL && setLightboxImage({ images: [photoURL], index: 0 })}
            />
            <label style={avatarEditBadge} aria-label="Change profile photo">
              <IoCamera size={13} />
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={e => pickAvatarFile(e.target.files[0])}
              />
            </label>
          </div>
          {uploading && <p style={uploadingText}>Uploading…</p>}
        </div>

        <div style={headerRight}>
          <div style={usernameRow}>
            <h2 style={usernameHeading}>
              {profileLoaded || username ? (
                <span style={usernameText}>@{username || "yourname"}</span>
              ) : (
                <span aria-hidden="true" style={usernameSkeleton} />
              )}
              {verified && <VerifiedBadge size={20} style={{ marginLeft: 4 }} />}
              <ReferralBadge count={referralCount} size={16} />
            </h2>

          </div>

          <div className="profile-stats-row" style={statsRow}>
            <Stat label={t("profile.posts")} value={postsCount} />
            <Stat label={t("profile.followers")} value={followerCount} onClick={() => setFollowListType("followers")} />
            <Stat label={t("profile.following")} value={followingCount} onClick={() => setFollowListType("following")} />
          </div>

          <div className="profile-secondary-stats" style={secondaryStatsRow}>
            <span style={statChip}><PiHeartFill style={{ color: "var(--accent-danger)" }} />{totalLikes} likes</span>
            <span style={statChip}><PiDownloadSimpleBold />{totalDownloads} downloads</span>
          </div>

          {notice && <div style={noticeBox}>{notice}</div>}
        </div>
       </div>

       {/* Editing opens in its own popup, like the settings menu, instead of
           unfolding inside the profile card. */}
       {editMode && (
         <Sheet
           title={t("profile.editProfile")}
           onClose={cancelEdit}
           busy={saving}
           footer={
             <div className="pf-actions">
               <button type="button" className="pf-cancel" onClick={cancelEdit} disabled={saving}>
                 Cancel
               </button>
               <button type="button" className="pf-save" onClick={saveProfile} disabled={saving}>
                 {saving ? "Saving…" : "Save"}
               </button>
             </div>
           }
         >
           {error && <div style={{ ...errorBox, marginTop: 8 }}>{error}</div>}

           <div className="pf-field">
             <label htmlFor="pf-username">Username</label>
             <div className="pf-inline">
               <span className="pf-at" aria-hidden="true">@</span>
               <input
                 id="pf-username"
                 value={draftUsername}
                 onChange={e => setDraftUsername(e.target.value)}
                 placeholder="username"
                 autoCapitalize="none"
                 autoCorrect="off"
                 spellCheck={false}
               />
             </div>
           </div>
           <p className="pf-hint">3–20 characters: lowercase letters, numbers, dots and underscores.</p>

           <div className="pf-field">
             <label htmlFor="pf-bio">Bio</label>
             <textarea
               id="pf-bio"
               placeholder="Add a bio…"
               value={draftBio}
               onChange={e => handleBioChange(e.target.value)}
               maxLength={BIO_CHAR_LIMIT}
             />
             <div className={`pf-count${draftBio.length >= BIO_CHAR_LIMIT - 10 ? " warn" : ""}`}>
               {draftBio.length}/{BIO_CHAR_LIMIT}
             </div>
           </div>

           <div className="pf-group">
             <div className="pf-cap">Gender</div>
             {/* update_own_profile uses COALESCE(new_gender, gender) like every
                 other field here, so passing null means "leave unchanged," not
                 "clear" — once set, there's no way to save it back to blank
                 through this RPC. That's fine: "Prefer not to say" is the
                 built-in escape hatch, so clicking an option only ever picks
                 one, never toggles back off. */}
             <div style={genderPillRow}>
               {GENDER_OPTIONS.map(opt => (
                 <button
                   type="button"
                   key={opt.value}
                   style={draftGender === opt.value ? genderPillActive : genderPill}
                   onClick={() => setDraftGender(opt.value)}
                 >
                   {opt.label}
                 </button>
               ))}
             </div>
           </div>
         </Sheet>
       )}

       <p style={bioFullWidth}>
         {bio}
         {bio && " "}
         <span style={bioEditLink} onClick={startBioEdit}>{bio ? "Edit" : "+ Add bio"}</span>
       </p>

       {bioEditMode && (
         <Sheet
           title="Edit bio"
           onClose={cancelBioEdit}
           busy={savingBio}
           footer={
             <div className="pf-actions">
               <button type="button" className="pf-cancel" onClick={cancelBioEdit} disabled={savingBio}>
                 Cancel
               </button>
               <button type="button" className="pf-save" onClick={saveBioOnly} disabled={savingBio}>
                 {savingBio ? "Saving…" : "Save"}
               </button>
             </div>
           }
         >
           {bioEditError && <div style={{ ...errorBox, marginTop: 8 }}>{bioEditError}</div>}

           <div className="pf-field">
             <label htmlFor="pf-bio-only">Bio</label>
             <textarea
               id="pf-bio-only"
               placeholder="Add a bio…"
               value={draftBio}
               onChange={e => handleBioChange(e.target.value)}
               maxLength={BIO_CHAR_LIMIT}
               autoFocus
             />
             <div className={`pf-count${draftBio.length >= BIO_CHAR_LIMIT - 10 ? " warn" : ""}`}>
               {draftBio.length}/{BIO_CHAR_LIMIT}
             </div>
           </div>
         </Sheet>
       )}

       {!nudgeDismissed && (!bio || !photoURL) && (
         <div style={nudgeBanner}>
           <span>Complete your profile — add {!photoURL && !bio ? "a photo and bio" : !photoURL ? "a photo" : "a bio"} so people know it's you.</span>
           <div style={{ display: "flex", gap: 12, flexShrink: 0 }}>
             <button style={nudgeActionBtn} onClick={startEdit}>
               Edit profile
             </button>
             <button
               style={nudgeDismissBtn}
               aria-label="Dismiss"
               onClick={() => {
                 setNudgeDismissed(true);
                 try {
                   localStorage.setItem("virsa_profile_nudge_dismissed", "1");
                 } catch {}
               }}
             >
               <IoClose size={16} />
             </button>
           </div>
         </div>
       )}
      </div>

      <hr style={divider} />

      {/* ===== POSTS ===== */}
      <div style={uploadsHeaderRow}>
        <h3 style={uploadsHeading}>{t("profile.posts")}</h3>
        {/* The app's own list instead of the phone's native selection popup. */}
        <PickerSelect
          label={t("profile.sortPosts")}
          value={sortBy}
          onChange={setSortBy}
          style={sortSelect}
          options={[
            { value: "newest", label: t("profile.sortNewest") },
            { value: "oldest", label: t("profile.sortOldest") },
            { value: "likes", label: t("profile.sortLikes") }
          ]}
        />
      </div>

      {postsLoading ? (
        <PostSkeleton />
      ) : visiblePosts.length === 0 ? (
        <p style={emptyText}>{t("profile.noPostsYet")}</p>
      ) : (
        <div className="profile-grid" style={grid}>
          {visiblePosts.map(d => {
            function updateDesign(fields) {
              setDesigns(prev =>
                prev.map(x => (x.id === d.id ? { ...x, ...fields } : x))
              );
            }

            return (
            <div key={d.id} style={card}>
              {/* ===== HEADER: your avatar + username + ⋮ menu ===== */}
              <div style={cardHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <img
                    src={photoURL || "/default-avatar.svg"}
                    alt={`${username || "Your"} avatar`}
                    style={cardAvatar}
                  />
                  <span style={postNameColumn}>
                    <span style={{ fontWeight: 700, fontSize: 18, color: "var(--text-primary)" }}>@{username}</span>
                    <PostTime date={d.created_at} />
                  </span>
                  {d.pinned && (
                    <span style={pinnedBadge}>
                      <IoPinOutline size={11} /> {t("profile.pinned")}
                    </span>
                  )}
                </div>

                <div style={{ position: "relative" }} ref={openTileMenuId === d.id ? tileMenuRef : null}>
                  <button
                    style={dotBtn}
                    onClick={() => setOpenTileMenuId(openTileMenuId === d.id ? null : d.id)}
                    aria-label="Post options"
                  >
                    <IoEllipsisVertical />
                  </button>

                  {openTileMenuId === d.id && (
                    <PostMenu
                      items={[
                        { icon: <IoCreateOutline />, label: "Edit", onClick: () => startEditDesign(d) },
                        {
                          icon: <IoPinOutline />,
                          label: d.pinned ? "Unpin from profile" : "Pin to profile",
                          onClick: () => togglePinDesign(d)
                        },
                        { icon: <IoTrashOutline />, label: "Delete", danger: true, onClick: () => handleDeleteDesign(d.id) }
                      ]}
                    />
                  )}
                </div>
              </div>

              {/* ===== IMAGE(S) ===== */}
              <ImageCarousel
                images={d.images?.length > 0 ? d.images : [d.image_url]}
                alt={d.title}
                imgStyle={{ ...cardImage, cursor: "zoom-in" }}
                onImageClick={setLightboxImage}
                onDoubleTap={() =>
                  likeDesign({ designId: d.id, user, designOwnerId: d.user_id, onUpdate: updateDesign, likeOnly: true })
                }
              />

              {/* ===== ACTION ROW ===== */}
              <div style={actionRow}>
                <LikeButton
                  designId={d.id}
                  likes={d.likes}
                  liked={d._likedByMe}
                  user={user}
                  designOwnerId={d.user_id}
                  onUpdate={updateDesign}
                />
                <button onClick={() => toggleComments(d.id)} style={commentBadge}>
                  <PiChatCircleBold size={27} /> <span style={commentBadgeCount}>{d.comment_count || 0}</span>
                </button>
                <DownloadButton
                  fileURL={d.design_url}
                  files={d.design_files}
                  designId={d.id}
                  downloads={d.downloads}
                  designOwnerId={d.user_id}
                  user={user}
                  onUpdate={updateDesign}
                />
                <span style={{ display: "inline-flex", marginLeft: "auto" }}>
                  <ShareButton designId={d.id} />
                </span>
              </div>

              {/* ===== CAPTION / EDIT FORM ===== */}
              <div style={editingDesignId === d.id ? { ...cardContent, paddingBottom: 14 } : cardContent}>
                {editingDesignId === d.id ? (
                  <>
                    <HashtagInput
                      multiline
                      rows={4}
                      value={designEditCaption}
                      onChange={setDesignEditCaption}
                      maxLength={TITLE_LIMIT}
                      placeholder="Write a caption… #phulkari #wedding #punjabi"
                    />
                    {designEditError && <div style={errorBox}>{designEditError}</div>}
                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                      <button onClick={() => saveEditDesign(d.id)} style={saveBtn}>
                        <IoCheckmark size={16} /> Save
                      </button>
                      <button onClick={cancelEditDesign} style={cancelBtn}>
                        <IoClose size={16} /> Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p style={{ margin: "0 0 4px", whiteSpace: "pre-wrap" }}>
                      <strong style={{ color: "var(--text-primary)", fontSize: 16 }}>@{username}</strong>{" "}
                      <TruncatedText text={d.title} />
                    </p>
                    {d.description && (
                      <p style={{ margin: "2px 0", color: "var(--text-body)", fontSize: 14 }}>
                        {d.description}
                      </p>
                    )}
                  </>
                )}

                {openComments[d.id] && (
                  <Comments designId={d.id} onClose={() => toggleComments(d.id)} designOwnerId={d.user_id} />
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {!postsLoading && hasMorePosts && <div ref={sentinelRef} style={{ height: 1 }} />}
      {loadingMorePosts && <p style={emptyText}>Loading more…</p>}

      {/* ===== RESET PASSWORD MODAL ===== */}
      {resetStage && (
        <div style={modalBackdrop} onClick={closeResetModal}>
          <div style={modalCard} ref={resetModalRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Reset your password" onClick={e => e.stopPropagation()}>
            <div style={brandRow}>
              <span style={brandMark}>GatherYou</span>
            </div>

            {resetStage === "verify" && (
              <>
                <h3 style={modalHeading}>Reset your password</h3>
                <p style={modalSubheading}>Confirming it's you before sending a code to <strong>{user.email}</strong>.</p>

                {resetError && <AlertBox tone="error">{resetError}</AlertBox>}

                <Turnstile ref={resetTurnstileRef} onVerify={handleResetCaptchaVerify} onExpire={() => setResetCaptchaToken(null)} />

                <button style={textLinkBtn} onClick={closeResetModal}>
                  Cancel
                </button>
              </>
            )}

            {resetStage === "otp" && (
              <>
                <h3 style={modalHeading}>Reset your password</h3>
                <p style={modalSubheading}>Sent to <strong>{user.email}</strong>.</p>

                {resetNotice && <AlertBox tone="success">{resetNotice}</AlertBox>}
                {resetError && <AlertBox tone="error">{resetError}</AlertBox>}

                <form onSubmit={onceWith(resetBusyRef, verifyResetOtp)}>
                  <label style={fieldLabel}>6-digit code</label>
                  <input
                    className="virsa-otp"
                    style={input}
                    inputMode="numeric"
                    maxLength={6}
                    autoFocus
                    placeholder="······"
                    value={resetOtp}
                    onChange={e => setResetOtp(e.target.value.replace(/\D/g, ""))}
                  />
                  <button type="submit" style={primaryBtn} disabled={resetLoading}>
                    {resetLoading ? "Verifying…" : "Verify"}
                  </button>
                </form>

                <Turnstile ref={resetTurnstileRef} onVerify={setResetCaptchaToken} onExpire={() => setResetCaptchaToken(null)} />

                <button style={textLinkBtn} disabled={!resetCaptchaToken} onClick={() => sendResetOtp(resetCaptchaToken)}>
                  Resend code
                </button>
                <button style={textLinkBtn} onClick={closeResetModal}>
                  Cancel
                </button>
              </>
            )}

            {resetStage === "newpassword" && (
              <>
                <h3 style={modalHeading}>Set a new password</h3>
                <p style={modalSubheading}>{PASSWORD_REQUIREMENTS_TEXT}</p>

                {resetError && <AlertBox tone="error">{resetError}</AlertBox>}

                <form onSubmit={onceWith(resetBusyRef, submitResetPassword)}>
                  <label style={fieldLabel}>New password</label>
                  <input
                    style={input}
                    type="password"
                    autoFocus
                    placeholder="New password"
                    value={resetNewPassword}
                    onChange={e => setResetNewPassword(e.target.value)}
                  />
                  <button type="submit" style={primaryBtn} disabled={resetLoading}>
                    {resetLoading ? "Saving…" : "Save new password"}
                  </button>
                </form>

                <button style={textLinkBtn} onClick={closeResetModal}>
                  Cancel
                </button>
              </>
            )}

            {resetStage === "done" && (
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 6, color: "var(--accent-success)" }}>
                  <IoCheckmarkCircle />
                </div>
                <h3 style={modalHeading}>Password updated</h3>
                <p style={modalSubheading}>You're all set.</p>
                <button style={primaryBtn} onClick={closeResetModal}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== CHANGE EMAIL MODAL ===== */}
      {emailChangeStage && (
        <div style={modalBackdrop} onClick={closeEmailChangeModal}>
          <div style={modalCard} ref={emailChangeModalRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Change your email" onClick={e => e.stopPropagation()}>
            <div style={brandRow}>
              <span style={brandMark}>GatherYou</span>
            </div>

            {emailChangeStage === "form" && (
              <>
                <h3 style={modalHeading}>Change your email</h3>
                <p style={modalSubheading}>Currently <strong>{user.email}</strong>.</p>

                {emailChangeError && <AlertBox tone="error">{emailChangeError}</AlertBox>}

                <form onSubmit={onceWith(emailChangeBusyRef, submitEmailChange)}>
                  <label style={fieldLabel}>New email</label>
                  <input
                    style={input}
                    type="email"
                    autoFocus
                    placeholder="you@example.com"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                  />
                  <button type="submit" style={primaryBtn} disabled={emailChangeLoading}>
                    {emailChangeLoading ? "Sending…" : "Send confirmation"}
                  </button>
                </form>

                <button style={textLinkBtn} onClick={closeEmailChangeModal}>
                  Cancel
                </button>
              </>
            )}

            {emailChangeStage === "verify-old" && (
              <>
                <h3 style={modalHeading}>Verify your current email</h3>
                <p style={modalSubheading}>
                  We sent a 6-digit code to your current email, <strong>{user.email}</strong>.
                </p>

                {emailChangeError && <AlertBox tone="error">{emailChangeError}</AlertBox>}

                <form onSubmit={onceWith(emailChangeBusyRef, verifyOldEmailOtp)}>
                  <label style={fieldLabel}>6-digit code</label>
                  <input
                    style={input}
                    inputMode="numeric"
                    maxLength={6}
                    autoFocus
                    placeholder="······"
                    value={oldEmailOtp}
                    onChange={e => setOldEmailOtp(e.target.value.replace(/\D/g, ""))}
                  />
                  <button type="submit" style={primaryBtn} disabled={emailChangeLoading}>
                    {emailChangeLoading ? "Verifying…" : "Verify"}
                  </button>
                </form>

                <button style={textLinkBtn} onClick={onceWith(emailChangeBusyRef, resendEmailChangeCodes)} disabled={emailChangeLoading}>
                  Resend codes
                </button>
                <button style={textLinkBtn} onClick={closeEmailChangeModal}>
                  Cancel
                </button>
              </>
            )}

            {emailChangeStage === "verify-new" && (
              <>
                <h3 style={modalHeading}>Verify your new email</h3>
                <p style={modalSubheading}>
                  Current email confirmed. Now enter the 6-digit code we sent to{" "}
                  <strong>{newEmail}</strong>.
                </p>

                {emailChangeError && <AlertBox tone="error">{emailChangeError}</AlertBox>}

                <form onSubmit={onceWith(emailChangeBusyRef, verifyNewEmailOtp)}>
                  <label style={fieldLabel}>6-digit code</label>
                  <input
                    style={input}
                    inputMode="numeric"
                    maxLength={6}
                    autoFocus
                    placeholder="······"
                    value={newEmailOtp}
                    onChange={e => setNewEmailOtp(e.target.value.replace(/\D/g, ""))}
                  />
                  <button type="submit" style={primaryBtn} disabled={emailChangeLoading}>
                    {emailChangeLoading ? "Verifying…" : "Verify"}
                  </button>
                </form>

                <button style={textLinkBtn} onClick={onceWith(emailChangeBusyRef, resendEmailChangeCodes)} disabled={emailChangeLoading}>
                  Resend codes
                </button>
                <button style={textLinkBtn} onClick={closeEmailChangeModal}>
                  Cancel
                </button>
              </>
            )}

            {emailChangeStage === "done" && (
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 32, marginBottom: 6, color: "var(--accent-success)" }}>
                  <IoCheckmarkCircle />
                </div>
                <h3 style={modalHeading}>Email changed</h3>
                <p style={modalSubheading}>
                  Your account email is now <strong>{newEmail}</strong>.
                </p>
                <button style={primaryBtn} onClick={closeEmailChangeModal}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===== VERIFIED BADGE REQUEST MODAL ===== */}
      {verifyModalOpen && (
        <div style={modalBackdrop} onClick={closeVerifyModal}>
          <div style={modalCard} ref={verifyModalRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Request a verified badge" onClick={e => e.stopPropagation()}>
            <div style={brandRow}>
              <span style={brandMark}>GatherYou</span>
            </div>

            <h3 style={modalHeading}>Request a verified badge</h3>
            <p style={modalSubheading}>Tell us why your account should be verified.</p>

            {verifyError && <AlertBox tone="error">{verifyError}</AlertBox>}

            <form onSubmit={submitVerificationRequest}>
              <label style={fieldLabel}>Reason</label>
              <textarea
                style={{ ...input, minHeight: 90, resize: "vertical" }}
                autoFocus
                placeholder="e.g. I'm a professional embroidery designer with an established following…"
                value={verifyReason}
                onChange={e => setVerifyReason(e.target.value)}
                maxLength={500}
              />
              <button type="submit" style={primaryBtn} disabled={verifySubmitting}>
                {verifySubmitting ? "Submitting…" : "Submit request"}
              </button>
            </form>

            <button style={textLinkBtn} onClick={closeVerifyModal}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {deleteToast && (
        <UndoToast
          // See Comments.jsx's identical fix — keyed on the design id so a
          // second delete while the first's undo toast is still up mounts a
          // fresh instance (with a full new 5s timer) instead of React
          // reusing the existing one, since the message text alone
          // ("Post deleted" every time) never changes to signal that.
          key={deleteToast.id}
          text={deleteToast.message}
          onUndo={() => undoDeleteDesign(deleteToast.id)}
          onExpire={() => setDeleteToast(null)}
        />
      )}

      {qrModalOpen && (
        <QRCodeModal
          value={`${window.location.origin}/u/${user.id}`}
          title={`@${username || "your profile"}`}
          onClose={() => setQrModalOpen(false)}
        />
      )}

      {/* ===== DELETE ACCOUNT MODAL ===== */}
      {deleteModalOpen && (
        <div style={modalBackdrop} onClick={closeDeleteModal}>
          <div style={modalCard} ref={deleteModalRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Delete your account" onClick={e => e.stopPropagation()}>
            <div style={brandRow}>
              <span style={brandMark}>GatherYou</span>
            </div>

            <h3 style={{ ...modalHeading, color: "var(--accent-danger)" }}>Delete your account</h3>
            <p style={modalSubheading}>
              This permanently deletes your profile, posts, comments, and
              follows. <strong>This can't be undone.</strong>
            </p>

            {deleteError && <AlertBox tone="error">{deleteError}</AlertBox>}

            <label style={fieldLabel}>Type DELETE to confirm</label>
            <input
              style={input}
              autoFocus
              placeholder="DELETE"
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              disabled={deleteLoading}
            />
            <button
              style={{ ...primaryBtn, background: "var(--accent-danger)" }}
              onClick={confirmDeleteAccount}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting…" : "Permanently delete my account"}
            </button>

            <button style={textLinkBtn} onClick={closeDeleteModal} disabled={deleteLoading}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {lightboxImage && (
        <ImageLightbox
          images={lightboxImage.images}
          initialIndex={lightboxImage.index}
          onClose={() => setLightboxImage(null)}
        />
      )}

      {followListType && (
        <FollowListModal
          userId={user.id}
          type={followListType}
          onClose={() => setFollowListType(null)}
        />
      )}

      {avatarCropSrc && (
        <ImageCropModal
          imageSrc={avatarCropSrc}
          fileName={avatarCropFileName}
          lockAspect
          onCancel={closeAvatarCrop}
          onSave={handleAvatarCropSave}
        />
      )}

      {settingsSlot &&
        createPortal(
          <div style={{ position: "relative" }} ref={menuRef}>
            <button onClick={() => setMenuOpen(o => !o)} style={{ ...dotBtn, fontSize: 28 }} aria-label="Settings">
              <IoSettings />
            </button>

            {menuOpen && (
              <>
              {/* Invisible full-viewport backdrop — overflow:hidden on body
                  alone doesn't reliably block touch-scroll on mobile, since
                  this dropdown has no backdrop of its own and the page peeks
                  out around its edges (see .profile-settings-menu). This
                  catches every touch so none of it reaches the page below. */}
              <div
                onClick={() => setMenuOpen(false)}
                style={{ position: "fixed", inset: 0, zIndex: 9, background: "transparent" }}
              />
              <div className="profile-settings-menu" ref={settingsMenuRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label="Settings" style={settingsBox}>
                <style>{settingsCss}</style>

                <SettingsSection title="Account">
                  <SettingsRow icon={<IoCreateOutline />} label={t("profile.editProfile")} onClick={startEdit} />
                  <SettingsRow icon={<IoKeyOutline />} label="Reset password" onClick={openPasswordReset} />
                  <SettingsRow icon={<IoMailOutline />} label="Change email" onClick={openEmailChange} />
                  {!verified && (
                    <SettingsRow
                      icon={<IoShieldCheckmarkOutline />}
                      label={verificationStatus === "pending" ? "Verification pending" : "Request verified badge"}
                      onClick={verificationStatus === "pending" ? undefined : openVerifyModal}
                      disabled={verificationStatus === "pending"}
                    />
                  )}
                </SettingsSection>

                <SettingsSection title="Share">
                  <ShareButton
                    url={`${window.location.origin}/u/${user.id}`}
                    text={`Check out @${username || "my"} designs on GatherYou`}
                    renderTrigger={open => (
                      <SettingsRow icon={<IoShareSocialOutline />} label="Share profile" onClick={open} />
                    )}
                  />
                  <ShareButton
                    url={`https://gatheryou.in/install?ref=${encodeURIComponent(username)}`}
                    text="Join me on GatherYou — discover and share embroidery designs"
                    renderTrigger={open => (
                      <SettingsRow
                        icon={<IoPersonAddOutline />}
                        label={referralCount > 0 ? `Invite friends · ${referralCount} joined` : "Invite friends"}
                        onClick={open}
                      />
                    )}
                  />
                  <SettingsRow
                    icon={<IoQrCodeOutline />}
                    label="Profile QR code"
                    onClick={() => { setMenuOpen(false); setQrModalOpen(true); }}
                  />
                  {installOption && (
                    <SettingsRow icon={<IoPhonePortraitOutline />} label="Install app" onClick={handleInstallApp} />
                  )}
                </SettingsSection>

                <SettingsSection title="Preferences">
                  <SettingsSwitch
                    icon={<IoEyeOutline />}
                    label="Show followers"
                    checked={showFollowers}
                    onClick={toggleShowFollowers}
                  />
                  {followersToggleError && <div style={toggleErrorText}>{followersToggleError}</div>}
                  <SettingsSwitch
                    icon={<IoEyeOutline />}
                    label="Show following"
                    checked={showFollowing}
                    onClick={toggleShowFollowing}
                  />
                  {followingToggleError && <div style={toggleErrorText}>{followingToggleError}</div>}
                  {pushSupported && (
                    <SettingsSwitch
                      icon={<IoNotificationsOutline />}
                      label="Push notifications"
                      checked={pushEnabled}
                      onClick={togglePush}
                    />
                  )}
                  {pushToggleError && <div style={toggleErrorText}>{pushToggleError}</div>}
                  <SettingsSwitch
                    icon={<IoMoonOutline />}
                    label="Dark mode"
                    checked={theme === "dark"}
                    onClick={toggleTheme}
                  />
                  <div className="sm-lang">
                    <div className="sm-lang-head">
                      <span className="sm-icon"><IoLanguageOutline /></span>
                      <span className="sm-label">Language</span>
                    </div>
                    <div className="sm-seg" role="group" aria-label="Language">
                      {SUPPORTED_LANGUAGES.map(l => (
                        <button
                          key={l.code}
                          type="button"
                          className={l.code === language ? "on" : ""}
                          aria-pressed={l.code === language}
                          onClick={() => setLanguage(l.code)}
                        >
                          {l.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </SettingsSection>

                <SettingsSection title="More">
                  <SettingsRow
                    icon={<IoSyncOutline />}
                    label="Switch account"
                    onClick={() => { setMenuOpen(false); navigate("/login"); }}
                  />
                  <SettingsRow
                    icon={<IoHelpCircleOutline />}
                    label="Help & FAQ"
                    onClick={() => { setMenuOpen(false); navigate("/help"); }}
                  />
                  <SettingsRow
                    icon={<IoDocumentTextOutline />}
                    label="Terms & Privacy"
                    onClick={() => { setMenuOpen(false); navigate("/terms"); }}
                  />
                  <SettingsRow
                    icon={<IoLogOutOutline />}
                    label="Logout"
                    onClick={async () => {
                      await supabase.auth.signOut();
                      clearLocalUserData();
                      disablePush();
                      setMenuOpen(false);
                      navigate("/");
                    }}
                  />
                  <SettingsRow icon={<IoTrashOutline />} label="Delete account" danger onClick={openDeleteModal} />
                </SettingsSection>
              </div>
              </>
            )}
          </div>,
          settingsSlot
        )}
    </div>
  );
}

export default Profile;

/* ===== SMALL BUILDING BLOCKS ===== */

function AlertBox({ tone, children }) {
  const palette = {
    error: { bg: "var(--alert-error-bg)", border: "var(--accent-danger)", text: "var(--alert-error-text)", Icon: IoWarningOutline },
    success: { bg: "var(--alert-success-bg)", border: "var(--accent-success)", text: "var(--alert-success-text)", Icon: IoCheckmarkCircle }
  }[tone];

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        background: palette.bg,
        border: `1px solid ${palette.border}33`,
        borderLeft: `3px solid ${palette.border}`,
        color: palette.text,
        borderRadius: 8,
        padding: "10px 12px",
        fontSize: 13,
        lineHeight: 1.4,
        margin: "10px 0"
      }}
    >
      <span aria-hidden="true" style={{ display: "flex", fontSize: 16, flexShrink: 0 }}>
        <palette.Icon />
      </span>
      <span>{children}</span>
    </div>
  );
}

/* ===== SETTINGS MENU PARTS ===== */

function SettingsSection({ title, children }) {
  return (
    <div className="sm-section">
      <div className="sm-title">{title}</div>
      {children}
    </div>
  );
}

function SettingsRow({ icon, label, onClick, danger, disabled }) {
  return (
    <button
      type="button"
      className={`sm-row${danger ? " danger" : ""}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="sm-icon">{icon}</span>
      <span className="sm-label">{label}</span>
    </button>
  );
}

function SettingsSwitch({ icon, label, checked, onClick }) {
  return (
    <button type="button" role="switch" aria-checked={checked} className="sm-row" onClick={onClick}>
      <span className="sm-icon">{icon}</span>
      <span className="sm-label">{label}</span>
      <span className={`sm-switch${checked ? " on" : ""}`}>
        <span className="sm-knob" />
      </span>
    </button>
  );
}

/* ===== COMPONENT ===== */

function Stat({ label, value, onClick }) {
  return (
    <div
      style={{ ...statItem, cursor: onClick ? "pointer" : "default" }}
      onClick={onClick}
    >
      <strong className="profile-stat-value" style={statValue}>{value}</strong>
      <div style={statLabel}>{label}</div>
    </div>
  );
}

/* ===== STYLES ===== */

const page = {
  padding: "24px 24px calc(90px + env(safe-area-inset-bottom))",
  maxWidth: 900,
  margin: "auto",
  minHeight: "100vh",
  fontFamily: "'Inter', Arial, sans-serif",
  background: "var(--bg-page)"
};

const header = {
  marginBottom: 20,
  background: "var(--bg-surface)",
  border: "1px solid var(--border-medium)",
  borderRadius: 18,
  padding: 24,
  boxShadow: "0 12px 30px rgba(74, 34, 24, 0.10)"
};

const topRow = {
  display: "flex",
  gap: 32,
  alignItems: "flex-start"
};

const brandRow = { display: "flex", alignItems: "baseline", gap: 8, marginBottom: 14 };
const brandMark = {
  fontFamily: "'Fraunces', serif",
  fontStyle: "italic",
  fontWeight: 600,
  fontSize: 18,
  color: "var(--accent-primary)"
};
const avatarWrap = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 6,
  flexShrink: 0
};

const avatar = {
  width: 96,
  height: 96,
  borderRadius: "50%",
  objectFit: "cover",
  border: "3px solid var(--bg-soft)"
};

const avatarEditBadge = {
  position: "absolute",
  bottom: 0,
  right: 0,
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  width: 28,
  height: 28,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 13,
  cursor: "pointer",
  border: "2px solid var(--bg-surface)"
};

const uploadingText = { fontSize: 12, color: "var(--text-muted)" };

const headerRight = { flex: 1, minWidth: 0 };

const usernameRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between"
};

const usernameHeading = {
  margin: 0,
  fontSize: 20,
  fontWeight: 800,
  color: "var(--text-heading)",
  display: "flex",
  alignItems: "center",
  gap: 6,
  minWidth: 0
};

const usernameText = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0
};

const usernameSkeleton = {
  display: "inline-block",
  width: 150,
  height: 24,
  borderRadius: 8,
  verticalAlign: "middle",
  background:
    "linear-gradient(110deg, var(--shimmer-base) 30%, var(--shimmer-highlight) 45%, var(--shimmer-base) 60%)",
  backgroundSize: "200% 100%",
  animation: "virsa-shimmer 1.4s ease-in-out infinite"
};

const dotBtn = {
  display: "flex",
  background: "none",
  border: "none",
  fontSize: 19,
  padding: 8,
  margin: -8, // the bigger tap area shouldn't move the icon
  borderRadius: "50%",
  cursor: "pointer",
  color: "var(--text-primary)"
};


const settingsBox = {
  position: "absolute",
  top: 34,
  right: 0,
  zIndex: 10,
  minWidth: 260,
  // Long list: scrolls inside itself instead of running off the bottom of the
  // screen (phones get their own bottom-sheet sizing from the media query).
  maxHeight: "calc(100vh - 110px)",
  overflowY: "auto",
  overscrollBehavior: "contain",
  padding: 6,
  background: "var(--bg-surface)",
  border: "1px solid var(--border-soft)",
  borderRadius: 16,
  boxShadow: "0 12px 32px rgba(46, 33, 24, 0.18)"
};

const settingsCss = `
  .sm-section + .sm-section { margin-top: 4px; padding-top: 2px; border-top: 1px solid var(--border-soft); }
  .sm-title {
    padding: 8px 10px 2px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-muted);
  }
  .sm-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 44px;
    padding: 6px 10px;
    border: none;
    border-radius: 10px;
    background: none;
    color: var(--text-heading);
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
  }
  .sm-row:hover, .sm-row:focus-visible { background: var(--bg-soft); outline: none; }
  .sm-row:disabled { opacity: 0.55; cursor: default; }
  .sm-row:disabled:hover { background: none; }
  .sm-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: var(--bg-soft);
    font-size: 16px;
    flex-shrink: 0;
  }
  .sm-row:hover .sm-icon, .sm-row:focus-visible .sm-icon { background: var(--bg-surface); }
  .sm-label { flex: 1; min-width: 0; }
  .sm-row.danger { color: var(--accent-danger); }
  .sm-row.danger .sm-icon { background: var(--alert-error-bg); }
  .sm-row.danger:hover, .sm-row.danger:focus-visible { background: var(--alert-error-bg); }
  .sm-switch {
    display: inline-flex;
    align-items: center;
    width: 40px;
    height: 24px;
    padding: 2px;
    border-radius: 12px;
    background: var(--border-input);
    flex-shrink: 0;
    transition: background 0.15s ease;
  }
  .sm-switch.on { background: var(--accent-primary); }
  .sm-knob {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--bg-surface);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    transition: transform 0.15s ease;
  }
  .sm-switch.on .sm-knob { transform: translateX(16px); }
  .sm-lang { padding: 6px 10px 8px; }
  .sm-lang-head { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; font-size: 14px; font-weight: 600; color: var(--text-heading); }
  .sm-seg {
    display: flex;
    gap: 2px;
    padding: 3px;
    border-radius: 999px;
    background: var(--bg-soft);
    border: 1px solid var(--border-soft);
  }
  .sm-seg button {
    flex: 1;
    padding: 6px 0;
    border: none;
    border-radius: 999px;
    background: transparent;
    color: var(--text-secondary);
    font: inherit;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
  }
  .sm-seg button.on { background: var(--accent-primary); color: var(--accent-contrast); }
`;

const toggleErrorText = {
  padding: "0 14px 10px",
  fontSize: 11.5,
  color: "var(--accent-danger)",
  borderBottom: "1px solid var(--border-soft)"
};








const statsRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  maxWidth: 360, // on a wide screen they stay a tidy group instead of spreading across the card
  margin: "12px 0"
};

const statItem = { textAlign: "center" };
const statValue = { fontSize: 20, fontWeight: 800, color: "var(--text-heading)", display: "block", lineHeight: 1.2 };
const statLabel = { fontSize: 12.5, fontWeight: 500, color: "var(--text-muted)", marginTop: 2 };

const bioFullWidth = {
  fontSize: 14,
  color: "var(--text-heading)",
  fontWeight: 500,
  lineHeight: 1.5,
  margin: "12px 0 0",
  whiteSpace: "pre-wrap"
};

const bioEditLink = {
  fontSize: 12.5,
  fontWeight: 700,
  color: "var(--accent-link)",
  cursor: "pointer",
  whiteSpace: "nowrap"
};

const nudgeBanner = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  marginTop: 14,
  padding: "10px 14px",
  borderRadius: 10,
  background: "var(--bg-soft)",
  border: "1px solid var(--border-soft)",
  fontSize: 13,
  color: "var(--text-secondary)"
};

const nudgeActionBtn = {
  padding: "6px 12px",
  borderRadius: 8,
  border: "none",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontWeight: 700,
  fontSize: 12.5,
  cursor: "pointer",
  flexShrink: 0
};

const nudgeDismissBtn = {
  display: "flex",
  background: "none",
  border: "none",
  color: "var(--text-muted)",
  cursor: "pointer",
  padding: 2,
  flexShrink: 0
};

const secondaryStatsRow = {
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  marginTop: 10
};

const statChip = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "4px 10px",
  borderRadius: 999,
  background: "var(--bg-soft)",
  fontSize: 12.5,
  fontWeight: 600,
  color: "var(--text-secondary)"
};


const errorBox = {
  background: "var(--alert-error-bg)",
  border: "1px solid var(--alert-error-border)",
  borderLeft: "3px solid var(--accent-danger)",
  color: "var(--alert-error-text)",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 13,
  marginBottom: 10
};

const noticeBox = {
  background: "var(--alert-success-bg)",
  border: "1px solid var(--alert-success-border)",
  borderLeft: "3px solid var(--accent-success)",
  color: "var(--alert-success-text)",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 13,
  marginTop: 10,
  maxWidth: 360
};

const fieldLabel = {
  display: "block",
  fontSize: 12.5,
  fontWeight: 700,
  color: "var(--text-secondary)",
  margin: "12px 0 6px"
};

const input = {
  width: "100%",
  boxSizing: "border-box",
  padding: "11px 12px",
  borderRadius: 12,
  border: "1.5px solid var(--border-input)",
  background: "var(--bg-surface)",
  color: "var(--text-heading)",
  fontSize: 16,
  fontFamily: "inherit",
  outline: "none"
};



const genderPillRow = {
  display: "flex",
  gap: 2,
  padding: 3,
  borderRadius: 999,
  background: "var(--bg-soft)",
  border: "1px solid var(--border-soft)"
};
const genderPill = {
  flex: "1 1 auto",
  padding: "10px 10px",
  borderRadius: 999,
  border: "none",
  background: "transparent",
  color: "var(--text-secondary)",
  fontSize: 13,
  fontWeight: 600,
  whiteSpace: "nowrap",
  cursor: "pointer"
};
const genderPillActive = {
  ...genderPill,
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)"
};

// Save / Cancel of the profile forms: equal, full-width, easy to hit

const saveBtn = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 18px",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 13.5
};

const cancelBtn = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 18px",
  background: "var(--border-soft)",
  color: "var(--text-secondary)",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
  fontSize: 13.5
};

const primaryBtn = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 10,
  border: "none",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontSize: 14,
  fontWeight: 700,
  cursor: "pointer",
  marginTop: 4
};

const textLinkBtn = {
  display: "block",
  width: "100%",
  textAlign: "center",
  background: "none",
  border: "none",
  color: "var(--accent-link)",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  padding: "8px 0"
};

const divider = { border: "none", borderTop: "1px solid var(--border-medium)", margin: "6px 0 12px" };

const uploadsHeaderRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  marginBottom: 8
};

const pinnedBadge = {
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  fontSize: 10.5,
  fontWeight: 700,
  color: "var(--accent-primary)",
  background: "var(--bg-soft)",
  padding: "2px 8px",
  borderRadius: 20,
};

// Same soft pill as the week/month switch on Trending.
const sortSelect = {
  width: "auto",
  padding: "5px 12px",
  fontSize: 12.5,
  fontWeight: 600,
  borderRadius: 999,
  border: "1px solid var(--border-soft)",
  background: "var(--bg-soft)",
  color: "var(--text-secondary)",
  marginBottom: 0
};

// Matches the headings on Trending and Saved: plain bold, not italic blue.
const uploadsHeading = {
  margin: 0,
  fontSize: 20,
  fontWeight: 800,
  color: "var(--text-heading)"
};
const emptyText = { textAlign: "center", color: "var(--text-muted)", padding: "40px 0" };

const grid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
  gap: "10px 20px"
};

const card = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-medium)",
  borderRadius: 14,
  overflow: "hidden",
  boxShadow: "0 6px 18px rgba(74,34,24,0.08)"
};

const cardHeader = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "4px 14px"
};

const cardAvatar = {
  width: 46,
  height: 46,
  borderRadius: "50%",
  objectFit: "cover",
  border: "1px solid var(--border-medium)"
};

const cardImage = {
  width: "100%",
  height: "auto",
  display: "block",
  background: "var(--border-soft)"
};

const actionRow = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "2px 14px 0"
};

const commentBadge = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  background: "none",
  border: "none",
  borderRadius: 20,
  height: 40,
  padding: "0 6px",
  fontSize: 14,
  fontWeight: 700,
  color: "var(--text-primary)",
  cursor: "pointer"
};

const commentBadgeCount = { fontWeight: "bold", fontSize: 13 };

const cardContent = { padding: "2px 14px 4px" };


/* ===== Reset-password modal ===== */

const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(46, 33, 24, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  zIndex: 100
};

const modalCard = {
  width: "100%",
  maxWidth: 360,
  background: "var(--bg-surface)",
  border: "1px solid var(--border-medium)",
  borderRadius: 16,
  padding: "22px 24px",
  boxShadow: "0 24px 60px rgba(74,34,24,0.25)"
};

const modalHeading = {
  fontFamily: "'Fraunces', serif",
  fontSize: 19,
  fontWeight: 600,
  color: "var(--text-heading)",
  margin: "0 0 4px"
};

const modalSubheading = { fontSize: 13.5, color: "var(--text-body)", margin: "0 0 14px" };
